import { reactive, computed, watch } from 'vue';

/**
 * useVoucherForm — shared form state and helpers for Create/Update voucher modals.
 *
 * @param {Object|import('vue').Ref|Function} initialValues — static object, ref, or () => values
 * @returns {Object} form, formErrors, discountPercentFilled, discountFixedFilled,
 *                    generateCode, resetForm
 */
export function useVoucherForm(initialValues = {}) {
    const formErrors = reactive({ name: null, code: null, units: null, discount: null });

    const resolve = () => {
        if (typeof initialValues === 'function') return initialValues();
        if (initialValues && typeof initialValues === 'object' && 'value' in initialValues) return initialValues.value;
        return initialValues;
    };

    const init = resolve();

    const form = reactive({
        name: init.name || '',
        code: init.code || '',
        units: init.units != null ? init.units : 1,
        discount_percent: init.discount_percent ?? null,
        discount_fixed: init.discount_fixed ?? null,
    });

    const discountPercentFilled = computed(() => {
        return form.discount_percent !== null
            && form.discount_percent !== ''
            && Number(form.discount_percent) !== 0;
    });

    const discountFixedFilled = computed(() => {
        return form.discount_fixed !== null
            && form.discount_fixed !== ''
            && Number(form.discount_fixed) !== 0;
    });

    function clearFormErrors() {
        formErrors.name = null;
        formErrors.code = null;
        formErrors.units = null;
        formErrors.discount = null;
    }

    watch(() => form.name, clearFormErrors);
    watch(() => form.code, clearFormErrors);
    watch(() => form.units, clearFormErrors);
    watch([() => form.discount_percent, () => form.discount_fixed], clearFormErrors);

    function generateCode() {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 8; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        form.code = code;
        clearFormErrors();
    }

    /** Restores form to current initialValues (re-evaluates resolver) */
    function resetForm() {
        const values = resolve();
        form.name = values.name || '';
        form.code = values.code || '';
        form.units = values.units != null ? values.units : 1;
        form.discount_percent = values.discount_percent ?? null;
        form.discount_fixed = values.discount_fixed ?? null;
        clearFormErrors();
    }

    return {
        form,
        formErrors,
        discountPercentFilled,
        discountFixedFilled,
        generateCode,
        resetForm,
    };
}
